/** Public path for Velvet (lab token JU) brand logo. */
export const VELVET_LOGO_SRC = "/Velvet%20Logo.png";

/** Public path for Uris Dental (partner `uris`, including the side admin assigned on Roles). */
export const URIS_LOGO_SRC = "/Uris%20Logo.png";

/** Velvet brand cream — navbar / primary wash. */
export const VELVET_ACCENT = "#FCF2E5";

/** Sidebar tan (slightly deeper than cream). */
export const VELVET_SIDEBAR_BG = "#E8D9C4";

/** Active sidebar row. */
export const VELVET_SIDEBAR_ACTIVE = "#D4C4A8";

/** Active sidebar left accent. */
export const VELVET_SIDEBAR_EDGE = "#8B7355";

/** Warm terracotta — logout / unit highlights. */
export const VELVET_WARM = "#A65D4A";

/** Dark ink from Velvet logo. */
export const VELVET_INK = "#2C2C2C";

/** Lab token that uses Velvet branding / JU portal behaviour. */
export const VELVET_TOKENS = new Set(["JU"]);

type BrandUser = {
  token?: string | null;
  partner?: string | null;
} | null | undefined;

/** True for JU lab token (incl. upload_token strings). */
export function isVelvetLabToken(token?: string | null): boolean {
  return !!token && VELVET_TOKENS.has(token);
}

export function isVelvetBrand(user: BrandUser): boolean {
  return isVelvetLabToken(user?.token);
}

/**
 * Uris portal theme. Clients get partner `uris` from the referral login.
 * The side admin is whoever is set to Side Admin with partner `uris` on the Roles page,
 * so their lab token can change in production.
 */
export function isUrisBrand(user: BrandUser): boolean {
  const partner = String(user?.partner || "").trim().toLowerCase();
  return partner === "uris";
}

/** Velvet visual theme only applies inside the authenticated dashboard. */
export function isVelvetDashboardPath(pathname?: string | null): boolean {
  return !!pathname?.startsWith("/dashboard");
}

/**
 * True when Velvet chrome (logo, cream/tan colours) should show.
 * Static/marketing pages always keep the normal ToothSketch look.
 */
export function isVelvetUiActive(
  user: BrandUser,
  pathname?: string | null
): boolean {
  return isVelvetBrand(user) && isVelvetDashboardPath(pathname);
}

/**
 * Resolves the navbar/footer brand logo for the logged-in user.
 * JU → Velvet logo only on /dashboard routes.
 */
export function getBrandLogoSrc(
  user: BrandUser,
  pathname?: string | null
): string {
  if (isVelvetUiActive(user, pathname)) return VELVET_LOGO_SRC;
  if (user?.partner === "bant") return "/bantlogo.svg";
  if (user?.partner === "elemdig") return "/elemlogo.svg";
  if (isUrisBrand(user)) return URIS_LOGO_SRC;
  return "/logo.png";
}

export function getBrandLogoAlt(
  user: BrandUser,
  pathname?: string | null
): string {
  if (isVelvetUiActive(user, pathname)) return "Velvet Logo";
  if (user?.partner === "bant") return "Bant Dental Logo";
  if (user?.partner === "elemdig") return "Element Digital Logo";
  if (isUrisBrand(user)) return "Uris Dental Logo";
  return "ToothSketch Logo";
}
